import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Package, 
  ShoppingCart, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Calendar,
  Search,
  Filter,
  Download,
  MoreVertical,
  ChevronDown
} from "lucide-react";

export function DesignDemo() {
  const [showNew, setShowNew] = useState(false);
  
  // Sample data for demonstration
  const stats = {
    totalOrders: 105,
    pendingOrders: 18,
    reviewedOrders: 42,
    sentToQuickbooksOrders: 45,
    totalItems: 8093,
    totalRevenue: "$45,231"
  };

  const orders = [
    { id: "180", customer: "Maple Leaf Tavern", items: 87, total: "$892.50", status: "pending", date: "Jan 15, 2025" },
    { id: "179", customer: "iGreek Bowmanville", items: 124, total: "$1,245.00", status: "reviewed", date: "Jan 15, 2025" },
    { id: "178", customer: "Boston Pizza", items: 65, total: "$687.25", status: "reviewed", date: "Jan 14, 2025" },
    { id: "177", customer: "Fresh Kitchen", items: 43, total: "$445.80", status: "pending", date: "Jan 14, 2025" },
  ];

  useEffect(() => {
    // Add/remove modern styles to body
    if (showNew) {
      document.body.classList.add('modern-demo');
    } else {
      document.body.classList.remove('modern-demo');
    }
    
    return () => {
      document.body.classList.remove('modern-demo');
    };
  }, [showNew]);

  if (!showNew) {
    // Current Design
    return (
      <div style={{ backgroundColor: '#F0EBE1', minHeight: '100vh' }}>
        {/* Current Header */}
        <div style={{ 
          backgroundColor: '#1D3D88', 
          padding: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1 style={{ 
            fontFamily: 'Rockwell, serif', 
            color: '#F0EBE1',
            fontSize: '2rem',
            margin: 0
          }}>
            FreshOrder - Current Design
          </h1>
          <Button 
            onClick={() => setShowNew(true)}
            style={{ 
              backgroundColor: '#41724D',
              color: 'white',
              fontFamily: 'Georgia, serif'
            }}
          >
            View Modern Design →
          </Button>
        </div>

        {/* Current Dashboard Content */}
        <div style={{ padding: '2rem' }}>
          <h2 style={{ 
            fontFamily: 'Rockwell, serif', 
            color: '#1D3D88',
            fontSize: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            Order Dashboard
          </h2>

          {/* Current Stats Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{ 
              backgroundColor: 'white',
              border: '2px solid #1D3D88',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontFamily: 'Georgia, serif', color: '#6B4F3B', fontSize: '0.875rem' }}>Total Orders</div>
              <div style={{ fontFamily: 'Rockwell, serif', color: '#1D3D88', fontSize: '2rem', fontWeight: 'bold' }}>105</div>
            </div>
            <div style={{ 
              backgroundColor: '#1D3D88',
              borderRadius: '8px',
              padding: '1rem',
              color: 'white'
            }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.875rem' }}>Pending</div>
              <div style={{ fontFamily: 'Rockwell, serif', fontSize: '2rem', fontWeight: 'bold' }}>18</div>
            </div>
            <div style={{ 
              backgroundColor: '#F59E0B',
              borderRadius: '8px',
              padding: '1rem',
              color: 'white'
            }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.875rem' }}>Reviewed</div>
              <div style={{ fontFamily: 'Rockwell, serif', fontSize: '2rem', fontWeight: 'bold' }}>42</div>
            </div>
            <div style={{ 
              backgroundColor: '#41724D',
              borderRadius: '8px',
              padding: '1rem',
              color: 'white'
            }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.875rem' }}>Delivered</div>
              <div style={{ fontFamily: 'Rockwell, serif', fontSize: '2rem', fontWeight: 'bold' }}>45</div>
            </div>
          </div>

          {/* Current Orders Table */}
          <div style={{ 
            backgroundColor: 'white',
            border: '2px solid #1D3D88',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', fontFamily: 'Georgia, serif' }}>
              <thead style={{ backgroundColor: '#1D3D88', color: 'white' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Order ID</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Customer</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Items</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={order.id} style={{ 
                    borderBottom: '1px solid #E5E7EB',
                    backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB'
                  }}>
                    <td style={{ padding: '0.75rem', color: '#1D3D88', fontWeight: 'bold' }}>{order.id}</td>
                    <td style={{ padding: '0.75rem', color: '#6B4F3B' }}>{order.customer}</td>
                    <td style={{ padding: '0.75rem', color: '#6B4F3B' }}>{order.items}</td>
                    <td style={{ padding: '0.75rem', color: '#6B4F3B' }}>{order.total}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        backgroundColor: order.status === 'pending' ? '#1D3D88' : 
                                        order.status === 'reviewed' ? '#F59E0B' : '#41724D',
                        color: 'white'
                      }}>
                        {order.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', color: '#6B4F3B' }}>{order.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Modern Design
  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .modern-demo * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        }
      `}</style>
      
      {/* Modern Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">FreshOrder - Modern Design</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              size="sm"
              className="border-gray-300"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              onClick={() => setShowNew(false)}
              size="sm"
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              ← Back to Current
            </Button>
          </div>
        </div>
      </header>

      {/* Modern Dashboard Content */}
      <main className="p-6">
        {/* Page Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Order Dashboard</h2>
            <p className="text-gray-500 mt-1">Track and manage your produce orders</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-gray-300">
              <Calendar className="w-4 h-4 mr-2" />
              This Week
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" size="sm" className="border-gray-300">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>

        {/* Modern Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOrders}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Pending</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{stats.pendingOrders}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Reviewed</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">{stats.reviewedOrders}</p>
                </div>
                <Package className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Delivered</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{stats.sentToQuickbooksOrders}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Items</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalItems.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalRevenue}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modern Orders Table */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-200 bg-gray-50/50">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg font-semibold text-gray-900">Recent Orders</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        #{order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.customer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {order.items}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.total}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge 
                          variant={order.status === 'pending' ? 'secondary' : 
                                  order.status === 'reviewed' ? 'outline' : 'default'}
                          className={
                            order.status === 'pending' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                            order.status === 'reviewed' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                            'bg-green-100 text-green-700 border-green-200'
                          }
                        >
                          {order.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {order.status === 'reviewed' && <Package className="w-3 h-3 mr-1" />}
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}